import React, {useState, useEffect} from 'react'
import { useSelector, useDispatch } from 'react-redux';

import { makeRequest, resetErrorMessage } from '../actions';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

import clsx from 'clsx';
import useStyles from '../styles/Styles';

import _ from 'lodash';
import * as yup from 'yup';

const RetrieveMemberNo = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [submitted, setSubmitted] = useState(false);
	const memberLabels = useSelector(state => state.settings.labels_members.data);
	const memberNoRequest = useSelector(state => state.requests.retrieve_member_no);
	useEffect(() => {
		if(memberNoRequest.submitting){
			setSubmitted(true);
		} 
	}, [memberNoRequest])

	const schema = yup.object().shape({
		password: yup.string().required('Please enter your password'),
		first_name: yup.string().required('Please enter your first name'),
		surname: yup.string().required('Please enter your surname'),
		year_of_birth: yup.string().required('Please enter your year of birth').matches(/^\d{4}$/, { message: 'Please enter a 4-digit year'})
	});
	
	const [data, setData] = useState({
		password: '',
		first_name: '',
		surname: '',
		year_of_birth: ''
	});
	
	const [validator, setValidator] = useState({
		password: { error: false, message: '' }, 
		first_name: { error: false, message: '' }, 
		surname: { error: false, message: '' },
		year_of_birth: { error: false, message: '' },
	});
	
	const [showPassword, setShowPassword] = useState(false);
	
	const storeAndValidate = (key, value) => {
		setData({...data, [key]: value});
		
		schema.validateAt(key, {[key]: value})
		.then(val => {
			setValidator({...validator, [key]: {error: false, message: ''}})
		})
		.catch(err => {
			setValidator({...validator, [key]: {error: true, message: err.message}})
		})
	}
	
	const fields = [
		{
			id: "password",
			atts: {
				label: "Password",
				type: showPassword ? "text" : "password",
				autoFocus: true,
				autoComplete: "off",
				InputProps: {
					endAdornment: (
					  <InputAdornment position="end">
						<IconButton
						  aria-label="toggle password visibility"
						  onClick={() => setShowPassword(!showPassword)}
						  onMouseDown={(event) => event.preventDefault()}
						  edge="end"
						>
						  {showPassword ? <Visibility /> : <VisibilityOff />}
						</IconButton>
					  </InputAdornment>
					)
				}
			}
		},
		{
			id: "first_name",
			atts: {
				label: "First name",
				autoComplete: "first_name",
				value: data.first_name
			}
		},
		{
		    id: "surname",
			atts: {
				label: memberLabels.surname,
				autoComplete: "surname",
				value: data.surname
			}
		},
		{
			id: "year_of_birth",
			atts: {			
				label: "Year of birth",
				autoComplete: "year_of_birth",
				value: data.year_of_birth
			}
		}
	];
	
	const maybeSubmit = () => {
		schema.validate(data, {abortEarly: false})
		.then((value) => {
			dispatch(makeRequest('retrieve_member_no', data));
		})
		.catch((err) => {
			const copyV = _.cloneDeep(validator);
			_.each(err.inner, (e) => {
				copyV[e.path].error = true;
				copyV[e.path].message = e.message;
			});
			setValidator(copyV);
		});
	}
	
	const SubmitResult = () => {
		if(memberNoRequest.submitting){
			return <Waiting
				message='Submitting your request. Please wait...'
				/>
		} else if(submitted && memberNoRequest.member_no){
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Member number retrieved.</Typography>
			<Typography paragraph>Your member number is: <strong>{memberNoRequest.member_no}</strong>.</Typography>
			</>
		} else if(submitted && !memberNoRequest.member_no) {
			return <>
			<Typography variant='h6' component='h3' gutterBottom>Failed to retrieve member number.</Typography>
			<Typography paragraph>Check the error message above.</Typography>
			</>;
		} else {
			return null;
		}
	}

	return (
	<>
	{submitted ?
	<ErrorDisplay
	extra_button={{
			atts: {
				onClick: () => {
					setSubmitted(false);
					setData({password: '',
						first_name: '',
						surname: '',
						year_of_birth: ''});
					dispatch(resetErrorMessage());
				}
			},
			text: 'Return to form',
		}}
	/> : 
	<ErrorDisplay />
	}
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<PageTitle title='Retrieve member number' />
	{!memberNoRequest.submitting && !submitted ? 
	<>
	<Typography variant="body2">
	Fill out and submit this form in order to retrieve your member number.
	</Typography>
	<form className={classes.form} autoComplete="off" noValidate> 
	<Grid container spacing={2}>
	{fields.map((f, i) => (
		<Grid item xs={12} key={i} >
		<TextField 
			variant="outlined"
			required
			fullWidth
			error={validator[f.id].error}
			helperText={validator[f.id].message}
			onChange={(event) => storeAndValidate(f.id, event.target.value )}
			{...f.atts}
			/>
		</Grid>
	))}
		<Grid item xs={12}>
		<Button
			type="button"
			fullWidth
			variant="contained"
			color="primary"
			className={classes.submit}
			onClick={() => maybeSubmit()}
		  >Submit
		  </Button>
		</Grid>
	</Grid>
	</form>
	</> :
	<SubmitResult />
	}
	</Paper>
	</>
)};

export default RetrieveMemberNo